<template>
  <div class="webrtc-view">
    <div class="webrtc-view__tracks">
      <div class="webrtc-view__not-connected" v-if="!isPatientCameraPlaying">
        <p class="webrtc-view__connecting-text" v-if="connecting">Connecting ...</p>
        <button v-if="!connecting" @click="startVideo()"
          class="button is-primary webrtc-view__connect-btn">Video Visit</button>
      </div>
      <div class="webrtc-view__video-container">
        <div class="open-mic-warning" v-show="!muted && isPatientCameraPlaying">
          MIC IS OPEN
        </div>
        <video v-if="isPatientCameraPlaying" ref="video" loop class="training-video"
          autoplay="autoplay" muted="muted" tabindex="-1" preload="auto" :src="meetingMetaData?.device.video_url"
          data-private></video>
        <div class="webrtc-view__video-controls" v-show="isPatientCameraPlaying">
          <button @click="stopVideo" class="button hangup-button webrtc-view__video-control">
            <i class="mdi mdi-phone-hangup" aria-hidden="true"></i>
          </button>
          <button @click="toggleMute" class="button webrtc-view__video-control">
            <i :class="['mdi', muted ? 'mdi-microphone-off' : 'mdi-microphone']" aria-hidden="true"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import to from "@/lib/to";
import { mapState, mapMutations } from "vuex";
import { markRaw } from "vue";
import { WebRTCSession } from "@/lib/webrtc";
import { describeWebrtcError } from "@/lib/webrtc/mediaErrors";
import { LOG_TYPES } from "@/lib/Log";

export default {
  name: "TrainingVideoView",
  props: {
    startAutomatically: {
      type: Boolean,
      required: false,
      default: false
    },
    videoSettings: {
      type: Object,
      required: false,
      default: () => {
        return {
          doorKnock: true,
          visitorName: '',
          activateVideo: false,
          isPreview: false,
        }
      }
    },
    meetingMetaData: {
      type: Object,
      required: false,
      default: () => { return {} }
    }
  },
  methods: {
    async startVideo() {
      this.log(LOG_TYPES.LIVE_VIDEO.CONNECT_STARTED)

      let videoReasonIds = []
      if (this.videoSettings.selectedVideoReasons && this.videoSettings.selectedVideoReasons.length > 0) {
        videoReasonIds = this.videoSettings.selectedVideoReasons.map(r => r.id)
      } else if (this.videoSettings.selectedVideoReason) {
        videoReasonIds = [this.videoSettings.selectedVideoReason]
      }

      let postData = {
        device_id: this.meetingMetaData.device.id,
        is_preview: this.videoSettings.isPreview,
        patient_id: this.meetingMetaData.patient?.id,
        play_greeting: this.videoSettings.doorKnock,
        ptz: this.meetingMetaData.is_ptz,
        two_way_video: this.videoSettings.activateVideo,
        video_reason_ids: videoReasonIds,
        virtual_request_id: this.meetingMetaData.virtual_request_id
      }

      const [err, response] = await to(this.http.post('/videos/join_meeting', postData, {
        handleErrors: true,
        skipDivisions: true
      }))

      if (!err) {
        const { data: joinData } = response
        this.meetingId = joinData.meeting_id
        this.$emit('meeting-id', this.meetingId)
      }

      this.log(LOG_TYPES.LIVE_VIDEO.CONNECT_SUCCEEDED)
      this.setPatientCameraIsPlaying(true)
      this.connecting = false
      const webrtcSession = new WebRTCSession();
      this.webrtcSession = markRaw(webrtcSession);
      const stream = await this.requestUserMedia({ audio: true, video: false }, 'training video')
      if (!stream) {
        this.stopVideo()
        return
      }
      this.localMediaStream = markRaw(stream)
    },
    stopVideo() {
      console.log("stopping training video...");
      if (this.webrtcSession) {
        this.webrtcSession.leaveMeetingRoom(this.$pusher);
        this.webrtcSession.disconnect();
        this.webrtcSession = null;
      }
      if (this.localMediaStream) {
        this.localMediaStream.getTracks().forEach((track) => {
          track.stop();
        });
        this.localMediaStream = null;
      }
      this.connecting = false;
      this.setPatientCameraIsPlaying(false)
      this.muted = false;
      this.$emit('close')
    },
    toggleMute() {
      if (this.$refs.video) {
        this.$refs.video.muted = this.muted
      }
      this.muted = !this.muted
    },
    ...mapMutations('LiveVideoStore', ['setPatientCameraIsPlaying']),
    log(type) {
      if (!this.meetingMetaData.test_mode) {
        const videoType = this.meetingMetaData.activateVideo ? 'monitoring' : 'meeting'
        let videoReasons = null
        let selectedVideoReasonIds = []

        if (this.videoSettings?.selectedVideoReason) {
          selectedVideoReasonIds = [this.videoSettings.selectedVideoReason]
        } else {
          selectedVideoReasonIds = this.videoSettings?.selectedVideoReasons?.map(x => x.id) || []
        }
        videoReasons = this.videoSettings?.availableVideoReasons?.filter(x => selectedVideoReasonIds.includes(x.id))?.map(x => x.name)?.join(', ')

        this.$logger.logLiveVideo(
          type,
          videoType,
          this.meetingMetaData?.patient?.id,
          this.meetingMetaData?.patient_monitor?.id,
          this.meetingId,
          this.meetingMetaData?.is_ptz,
          videoReasons
        )
      }
    },
    async requestUserMedia(constraints, context = '') {
      const [err, stream] = await to(navigator.mediaDevices.getUserMedia(constraints))
      if (err) {
        console.error(`Unable to get user media${context ? ` for ${context}` : ''}`, err)
        this.$buefy.dialog.alert({
          title: 'Media Error',
          message: describeWebrtcError(err),
          type: 'is-danger'
        })
        return null
      }
      return stream
    },
  },
  computed: {
    ...mapState('LiveVideoStore', ['isPatientCameraPlaying', 'requestVideoStart']),
  },
  mounted() {
    if (this.startAutomatically) {
      this.startVideo()
    }
  },
  created() {
    window.addEventListener('beforeunload', this.stopVideo);
  },
  beforeUnmount() {
    window.removeEventListener('beforeunload', this.stopVideo)
  },
  watch: {
    requestVideoStart: {
      handler(newValue) {
        if (newValue && !this.startAutomatically) {
          this.startVideo()
        }
      },
      immediate: true
    }
  },
  data() {
    return {
      connecting: false,
      webrtcSession: null,
      muted: false,
      meetingId: null,
      localMediaStream: null,
    }
  }
}
</script>

<style scoped lang="scss">
.webrtc-view__tracks {
  position: relative;
  width: 100%;
  height: 100%;
}

.webrtc-view__not-connected {
  position: relative;
  top: 0;
  width: 100%;
  height: 100%;
  background-image: url("@/assets/blurry_hospital.jpg");
  background-size: cover;
  background-position: center center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  button {
    margin-top: 10px;
  }
}

.webrtc-view__video-controls {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.hangup-button {
  background-color: rgb(247 79 79 / 56%) !important;
  border: solid 1px #ff7d7d !important;

  &:hover {
    background-color: #ff7d7d !important;
  }
}

.webrtc-view__video-control {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  margin: 0 10px;
  background-color: #4fc3f78f;
  color: #fff;
  border: solid 1px #7dd7ff;
  opacity: 0;
  transition: all 0.1s linear;
  box-shadow: none !important;

  i {
    padding-top: 5px;
    font-size: 23px;
  }
}

.webrtc-view__video-control:hover:not(:disabled) {
  background-color: #4fc3f7;
  color: #fff;
  border: solid 1px #7dd7ff;
}

.webrtc-view__connecting-text {
  font-size: 13px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.webrtc-view__video-container {
  position: relative;
  overflow: hidden;
  width: 100%;
  height: 100%;

  &:hover .webrtc-view__video-control {
    opacity: 1;
  }
}

.training-video {
  width: 100%;
}

.open-mic-warning {
  position: absolute;
  width: 100%;
  left: 0;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 18;
  font-size: 11px;
  letter-spacing: 2px;
  font-weight: bold;
  color: #fff;
  padding: 3px;
  background: red;
}

@include until($desktop) {
  .webrtc-view__video-controls {
    position: absolute;
    top: initial;
    bottom: 10px;
  }

  .webrtc-view__video-control {
    opacity: 1 !important;
  }
}
</style>
